import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Paperclip } from "lucide-react";
import fetchWithCredentials, {
  connectToWebSocket,
  toCamelCase,
  uploadFile,
} from "apiClient";
import { MessageBalloon } from "./MessageBalloon";
import Message from "types/Message";
import { Button } from "./ui/Button";
import { useAuth } from "context/AuthContext";

interface FloatingChatProps {
  teacherId: number;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export function FloatingChat({
  teacherId,
  isOpen: externalIsOpen,
  onToggle,
}: FloatingChatProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;
  const [page, setPage] = useState(1);
  const [messageToSend, setMessageToSend] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [wasManuallyClosed, setWasManuallyClosed] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);

  const { email } = useAuth();

  // Mock contact data for now - this would come from the backend
  const contact = {
    id: 1,
    name: "Teacher Contact",
    email: email || "teacher@example.com",
    isFakeChannel: true,
    unreadCount: 0,
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const loadedPages = useRef<Set<number>>(new Set());
  const scrollHeightBeforeLoad = useRef<number>(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  // Mark messages as read when chat is opened
  const markMessagesAsRead = async () => {
    try {
      await fetchWithCredentials(`/api/teacher/chat/mark-read/`, {
        method: "POST",
      });
      setHasUnreadMessages(false);
    } catch (err) {
      console.error("Failed to mark messages as read:", err);
    }
  };

  // Check for unread messages on component mount
  useEffect(() => {
    const checkUnreadMessages = async () => {
      try {
        const data = await fetchWithCredentials(`/api/teacher/chat/?page=1`);

        if (!data || !data.results) {
          return;
        }

        const hasUnread = (data.results || []).some((msg: any) => !msg.read);
        setHasUnreadMessages(hasUnread);

        // Auto-open chat if there are unread messages and it wasn't manually closed
        if (hasUnread && !wasManuallyClosed) {
          setIsOpen(true);
        }
      } catch (err) {
        console.error("Failed to check unread messages:", err);
      }
    };

    checkUnreadMessages();
  }, [wasManuallyClosed, setIsOpen]);

  // Fetch messages when chat is opened
  useEffect(() => {
    if (!isOpen) return;

    const fetchMessages = async () => {
      setLoading(true);
      setError(null);

      // Store scroll height before loading new messages (for pagination)
      if (page > 1) {
        scrollHeightBeforeLoad.current =
          scrollContainerRef.current?.scrollHeight || 0;
      }

      try {
        const data = await fetchWithCredentials(
          `/api/teacher/chat/?page=${page}`
        );

        if (page === 1) {
          setAllMessages(data.results || []);
          const hasUnread = (data.results || []).some((msg: any) => !msg.read);
          setHasUnreadMessages(hasUnread);
          // Check if there are more pages available
          setHasMorePages(data.next !== null);
        } else {
          // Only add new messages if there are any
          if (data.results && data.results.length > 0) {
            setAllMessages((prev) => [...prev, ...data.results]);
            // Check if there are more pages available
            setHasMorePages(data.next !== null);
          } else {
            // No more results, no more pages
            setHasMorePages(false);
          }
        }

        // Only mark page as loaded if we got results or it's page 1
        if (page === 1 || (data.results && data.results.length > 0)) {
          loadedPages.current.add(page);
        }
      } catch (err: any) {
        console.error("Failed to fetch messages:", err);

        // Handle 404 errors specifically (no more pages)
        if (err.message.includes("404") || err.message.includes("Not Found")) {
          setHasMorePages(false);
          loadedPages.current.add(page); // Mark this page as "loaded" to prevent retries
          setLoading(false);
          return;
        }

        // Provide more specific error messages
        if (err.message.includes("Network") || err.message.includes("fetch")) {
          setError(
            "Network error. Please check your connection and try again."
          );
        } else if (
          err.message.includes("401") ||
          err.message.includes("Unauthorized")
        ) {
          setError("Session expired. Please refresh the page.");
        } else if (
          err.message.includes("500") ||
          err.message.includes("Internal Server Error")
        ) {
          setError("Server error. Please try again later.");
        } else {
          setError(err.message || "Failed to load messages. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [isOpen, page]);

  // Maintain scroll position when loading older messages
  useEffect(() => {
    if (
      page > 1 &&
      scrollContainerRef.current &&
      scrollHeightBeforeLoad.current > 0
    ) {
      // Use a small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const scrollDiff = newScrollHeight - scrollHeightBeforeLoad.current;

          // Adjust scroll position to maintain the same relative position
          if (scrollDiff > 0) {
            container.scrollTop = scrollDiff;
          }
        }

        // Reset the stored height
        scrollHeightBeforeLoad.current = 0;
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [allMessages, page]);

  // WebSocket connection for real-time messages
  useEffect(() => {
    if (!isOpen) return;

    const socket = connectToWebSocket(`/ws/teacher/chat/`, (event) => {
      const message = toCamelCase(JSON.parse(event.data));
      setAllMessages((prev) => {
        const filtered = prev.filter(
          (m) => !(m.id < 0 && m.content === message.content)
        );
        return [message, ...filtered].sort(
          (a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt)
        );
      });
      setHasUnreadMessages(true);
    });

    return () => socket.close();
  }, [isOpen]);

  // Scroll to bottom when new messages arrive (only for page 1 or new messages)
  useEffect(() => {
    if (isOpen && allMessages.length > 0 && page === 1) {
      // Use a small delay to ensure messages are rendered
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, allMessages, page]);

  // Scroll listener to load more messages when reaching top
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loading) return;

    // Only try to load more if we're near the top and not already loading
    if (container.scrollTop < 50) {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce the page loading to prevent multiple rapid requests
      scrollTimeoutRef.current = setTimeout(() => {
        // Check if we should load the next page
        const nextPage = page + 1;

        // Only load if we haven't already loaded this page and there are more pages
        // Also check if we're not already loading and there are more pages
        if (!loadedPages.current.has(nextPage) && hasMorePages && !loading) {
          setPage(nextPage);
        }
      }, 150); // 150ms debounce
    }
  }, [loading, page, hasMorePages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
        // Clear any pending scroll timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }
      };
    }
  }, [handleScroll]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError("File is too large (max 50MB)");
        setSelectedFile(null);
      } else {
        setFileError(null);
        setSelectedFile(file);
      }
    }
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const sendMessage = async (content: string, fileUrl?: string | null) => {
    const body: any = { content };
    if (fileUrl) body.fileUrl = fileUrl;

    try {
      await fetchWithCredentials(`/api/teacher/chat/`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    } catch (err: any) {
      console.error("Failed to send message:", err);
      throw err;
    }
  };

  const handleSend = async () => {
    const content = messageToSend.trim();
    if (!content && !selectedFile) return;

    // Clear input immediately for better UX
    const currentMessage = messageToSend;
    const currentFile = selectedFile;
    setMessageToSend("");
    setSelectedFile(null);
    setFileError(null);

    let fileUrl: string | null = null;
    if (currentFile) {
      setUploading(true);
      try {
        fileUrl = await uploadFile(currentFile);
      } catch {
        setFileError("Error uploading file");
        setUploading(false);
        // Restore the message and file on upload error
        setMessageToSend(currentMessage);
        setSelectedFile(currentFile);
        return;
      }
      setUploading(false);
    }

    const optimisticMessage: Message = {
      id: -Date.now(),
      type: "user",
      content,
      fileUrl,
      sentAt: new Date().toISOString(),
    };

    setAllMessages((prev) => [optimisticMessage, ...prev]);

    try {
      await sendMessage(content, fileUrl);
    } catch (err) {
      console.error("Failed to send message:", err);
      setAllMessages((prev) =>
        prev.filter((m) => m.id !== optimisticMessage.id)
      );
      // Restore the message and file on send error
      setMessageToSend(currentMessage);
      if (currentFile) {
        setSelectedFile(currentFile);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !e.metaKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleChat = () => {
    if (isOpen) {
      markMessagesAsRead();
      setWasManuallyClosed(true);
    } else {
      setWasManuallyClosed(false);
    }

    setIsOpen(!isOpen);
    if (!isOpen) {
      setPage(1);
      loadedPages.current = new Set();
      setHasMorePages(true);
      // Clear any pending scroll timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    }
  };

  // Don't render if not a fake channel
  if (!contact.isFakeChannel) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 lg:bottom-4 lg:right-4 z-30 lg:ml-0 ml-16">
      {/* Floating Chat Icon */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="w-14 h-14 lg:w-16 lg:h-16 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center relative transition-colors"
          title="Chat with Eduvia"
        >
          <MessageCircle className="w-6 h-6 lg:w-8 lg:h-8" />
          {hasUnreadMessages && (
            <span className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 w-5 h-5 lg:w-7 lg:h-7 text-xs lg:text-sm font-bold text-white bg-red-600 rounded-full flex items-center justify-center">
              !
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed lg:relative bottom-0 right-0 lg:bottom-auto lg:right-auto w-full h-full lg:w-[500px] lg:h-[600px] bg-white rounded-t-lg lg:rounded-lg shadow-xl border border-gray-200 flex flex-col z-30 max-w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-white rounded-full flex items-center justify-center border-2 border-blue-200 shadow-sm ml-12 lg:ml-0 flex-shrink-0">
                <img
                  src="logo192.png"
                  alt="Eduvia Logo"
                  className="w-6 h-6 lg:w-8 lg:h-8 object-contain"
                />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900 truncate">
                  Eduvia
                </h3>
                <p className="text-xs lg:text-sm text-gray-500 truncate">
                  AI Assistant
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={toggleChat}
                className="p-2 hover:bg-gray-200 rounded transition-colors mr-12 lg:mr-0"
                title="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4 bg-gray-50"
          >
            {loading && page === 1 ? (
              <div className="text-center text-sm lg:text-base text-gray-500">
                Loading...
              </div>
            ) : allMessages.length > 0 ? (
              [...allMessages]
                .reverse()
                .map((msg) => <MessageBalloon key={msg.id} message={msg} />)
            ) : (
              <div className="text-center text-sm lg:text-base text-gray-500">
                Start a conversation with Eduvia
              </div>
            )}
            {loading && page > 1 && (
              <div className="text-center text-sm lg:text-base text-gray-500">
                Loading older messages...
              </div>
            )}
            {error && (
              <div className="text-center p-3 lg:p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-sm lg:text-base text-red-600 mb-2">
                  {error}
                </div>
                <button
                  onClick={() => {
                    setError(null);
                    setPage(1);
                    loadedPages.current = new Set();
                    setHasMorePages(true);
                  }}
                  className="text-xs lg:text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-4 lg:p-6 border-t border-gray-200 bg-white rounded-b-lg">
            {selectedFile && (
              <div className="flex items-center mb-3 bg-gray-100 rounded-lg px-3 py-2 text-xs lg:text-sm">
                <span className="truncate max-w-xs">{selectedFile.name}</span>
                <button onClick={handleRemoveFile} className="ml-2">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            )}
            {fileError && (
              <div className="text-xs lg:text-sm text-red-500 mb-3">
                {fileError}
              </div>
            )}
            <div className="flex items-end space-x-2 lg:space-x-3">
              <div className="flex-1">
                <textarea
                  value={messageToSend}
                  onChange={(e) => setMessageToSend(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className="w-full border border-gray-300 rounded-lg p-2 lg:p-3 text-sm lg:text-base resize-none h-10 lg:h-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={uploading}
                />
              </div>
              <div className="flex items-center space-x-1 lg:space-x-2">
                <label className="cursor-pointer p-2 lg:p-3 hover:bg-gray-100 rounded-lg transition-colors">
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  <Paperclip className="w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                </label>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleSend}
                  disabled={
                    uploading || (!messageToSend.trim() && !selectedFile)
                  }
                  className="h-10 lg:h-12 px-3 lg:px-6"
                >
                  {uploading ? (
                    <div className="w-4 h-4 lg:w-5 lg:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 lg:w-5 lg:h-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
